import { useEffect, useState, useCallback } from 'react';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { useNavigate } from 'react-router-dom';
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { TwoFactorChallenge } from "@/components/auth/TwoFactorChallenge";

const LoginPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showMfaChallenge, setShowMfaChallenge] = useState(false);
  const [mfaFactorId, setMfaFactorId] = useState<string | null>(null);

  // Check if MFA is required for the current session
  const checkMfaRequired = useCallback(async () => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return false;

    const { data: aalData, error: aalError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

    if (aalError) {
      console.error('[LoginPage] AAL check error:', aalError);
      return false;
    }

    // If current AAL is aal1 but next level is aal2, MFA is required
    if (aalData?.currentLevel === 'aal1' && aalData?.nextLevel === 'aal2') {
      // Get the TOTP factor
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      const totpFactor = factorsData?.totp?.[0];

      if (totpFactor) {
        setMfaFactorId(totpFactor.id);
        setShowMfaChallenge(true);
        return true;
      }
    }

    return false;
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error) {
        console.error('Session check error:', error);
        let errorMessage = error.message;
        try {
          if (error.message.includes('body')) {
            const bodyMatch = /"body":"([^"]+)"/.exec(error.message);
            if (bodyMatch) {
              const parsedBody = JSON.parse(bodyMatch[1].replace(/\\"/g, '"'));
              errorMessage = parsedBody.message;
            }
          }
        } catch (e) {
          console.debug('Error parsing error body:', e);
        }

        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        });
        return;
      }
      if (session) {
        // Check if MFA verification is needed
        const mfaRequired = await checkMfaRequired();
        if (!mfaRequired) {
          navigate('/');
        }
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        // Check if MFA is required before proceeding
        const mfaRequired = await checkMfaRequired();
        if (mfaRequired) {
          return; // Stay on login page showing MFA challenge
        }

        toast({
          title: "Welcome back!",
          description: "You have successfully signed in.",
        });
        navigate('/');
      }
      
      if (event === 'SIGNED_OUT') {
        toast({
          title: "Signed out",
          description: "You have been signed out successfully.",
        });
      }

      if (event === 'USER_UPDATED') {
        toast({
          title: "Account updated",
          description: "Your account has been updated successfully.",
        });
      }

      if (event === 'INITIAL_SESSION') {
        if (!session) {
          toast({
            title: "Welcome",
            description: "Please sign in to continue.",
          });
        }
      }

      if (!session && event === 'SIGNED_OUT') {
        const error = (session as any)?.error;
        if (error) {
          let errorMessage;
          try {
            if (error.message.includes('body')) {
              const bodyMatch = /"body":"([^"]+)"/.exec(error.message);
              if (bodyMatch) {
                const parsedBody = JSON.parse(bodyMatch[1].replace(/\\"/g, '"'));
                errorMessage = parsedBody.message;
              } else {
                errorMessage = error.message;
              }
            } else {
              errorMessage = error.message;
            }
          } catch (e) {
            errorMessage = error.message || 'An authentication error occurred';
          }

          toast({
            title: "Authentication Error",
            description: errorMessage,
            variant: "destructive",
          });
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate, toast, checkMfaRequired]);

  const handleMfaSuccess = () => {
    toast({
      title: "Welcome back!",
      description: "You have successfully signed in.",
    });
    navigate('/');
  };

  const handleMfaBack = async () => {
    // Sign out to return to login form
    await supabase.auth.signOut();
    setShowMfaChallenge(false);
    setMfaFactorId(null);
  };

  // Show MFA challenge if required
  if (showMfaChallenge && mfaFactorId) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <TwoFactorChallenge
          factorId={mfaFactorId}
          onSuccess={handleMfaSuccess}
          onBack={handleMfaBack}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="w-full max-w-md mx-auto bg-white">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Welcome to Calie.ai</CardTitle>
          <CardDescription className="text-center">
            Sign in to manage your phone settings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Auth
            supabaseClient={supabase}
            appearance={{
              theme: ThemeSupa,
              variables: {
                default: {
                  colors: {
                    brand: '#2563eb',
                    brandAccent: '#1d4ed8',
                  },
                },
              },
            }}
            theme="light"
            providers={[]}
            localization={{
              variables: {
                sign_in: {
                  email_label: 'Email',
                  password_label: 'Password',
                  button_label: 'Sign in',
                  loading_button_label: 'Signing in...',
                  email_input_placeholder: 'Your email address',
                  password_input_placeholder: 'Your password',
                  link_text: 'Already have an account? Sign in',
                },
                sign_up: {
                  email_label: 'Email',
                  password_label: 'Password',
                  button_label: 'Sign up',
                  loading_button_label: 'Signing up...',
                  email_input_placeholder: 'Your email address',
                  password_input_placeholder: 'Your password',
                  link_text: "Don't have an account? Sign up",
                },
              },
            }}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;